import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle2 } from 'lucide-react';
import { CONTACT_INFO } from '../mock/mock';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    projectType: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission — stored to localStorage so it feels real without a backend
    const existing = JSON.parse(localStorage.getItem('rcd_enquiries') || '[]');
    existing.push({ ...form, ts: new Date().toISOString() });
    localStorage.setItem('rcd_enquiries', JSON.stringify(existing));
    setSubmitted(true);
    toast({
      title: 'Enquiry received',
      description: 'Thank you. Our atelier team will reach out within one business day.'
    });
    setForm({ name: '', email: '', company: '', projectType: '', message: '' });
    setTimeout(() => setSubmitted(false), 4000);
  };

  return (
    <div>
      <section className="pt-24 pb-16 bg-[#f2ebde]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-xs tracking-[0.32em] uppercase text-[#a67c3d] mb-5">Contact</p>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-[#1a1614] leading-[1.05] max-w-4xl">
            Begin with the brief. <span className="italic text-[#a67c3d]">We&rsquo;ll take it from there.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-[#4a3f36] leading-relaxed">
            Share a drawing, moodboard, specification, reference or timeline &mdash; whatever stage the project has reached. The team will review the material and respond around the requirements of the brief.
          </p>
        </div>
      </section>

      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
          {/* Info */}
          <aside className="lg:col-span-4 space-y-10">
            <div>
              <h2 className="font-serif text-2xl text-[#1a1614] mb-6">RCD Jaipur</h2>
              <ul className="space-y-5 text-sm text-[#3a2f27]">
                <li className="flex gap-4">
                  <MapPin size={18} className="text-[#a67c3d] flex-shrink-0 mt-0.5" />
                  <span className="leading-relaxed">{CONTACT_INFO.address}</span>
                </li>
                <li className="flex gap-4">
                  <Phone size={18} className="text-[#a67c3d] flex-shrink-0 mt-0.5" />
                  <span>{CONTACT_INFO.phone}</span>
                </li>
                <li className="flex gap-4">
                  <Mail size={18} className="text-[#a67c3d] flex-shrink-0 mt-0.5" />
                  <span>{CONTACT_INFO.email}</span>
                </li>
                <li className="flex gap-4">
                  <Clock size={18} className="text-[#a67c3d] flex-shrink-0 mt-0.5" />
                  <span>{CONTACT_INFO.hours}</span>
                </li>
              </ul>
            </div>

            <div className="border-t border-[#e0d6c5] pt-8">
              <h3 className="font-serif text-lg text-[#1a1614] mb-3">Working with us</h3>
              <p className="text-sm text-[#4a3f36] leading-relaxed">
                RCD reviews each enquiry against its scope, development requirements, quantities, destination and intended timeline before confirming the appropriate next step.
              </p>
            </div>
          </aside>

          {/* Form */}
          <div className="lg:col-span-8 bg-white p-8 md:p-12 border border-[#e8dfd1]">
            {submitted && (
              <div className="mb-6 flex items-center gap-3 p-4 bg-[#f2ebde] border-l-2 border-[#a67c3d]">
                <CheckCircle2 size={18} className="text-[#a67c3d]" />
                <p className="text-sm text-[#3a2f27]">Thank you — your enquiry has been received.</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs tracking-[0.2em] uppercase text-[#6b5847] mb-2">Your Name</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={form.name}
                    onChange={handleChange}
                    className="w-full px-0 py-3 bg-transparent border-b border-[#c9bfae] focus:border-[#1a1614] outline-none text-[#1a1614] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs tracking-[0.2em] uppercase text-[#6b5847] mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={form.email}
                    onChange={handleChange}
                    className="w-full px-0 py-3 bg-transparent border-b border-[#c9bfae] focus:border-[#1a1614] outline-none text-[#1a1614] transition-colors"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs tracking-[0.2em] uppercase text-[#6b5847] mb-2">Company / Studio</label>
                  <input
                    type="text"
                    name="company"
                    value={form.company}
                    onChange={handleChange}
                    className="w-full px-0 py-3 bg-transparent border-b border-[#c9bfae] focus:border-[#1a1614] outline-none text-[#1a1614] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs tracking-[0.2em] uppercase text-[#6b5847] mb-2">Project Type</label>
                  <select
                    name="projectType"
                    value={form.projectType}
                    onChange={handleChange}
                    className="w-full px-0 py-3 bg-transparent border-b border-[#c9bfae] focus:border-[#1a1614] outline-none text-[#1a1614] transition-colors"
                  >
                    <option value="">Select…</option>
                    <option>Palace / Heritage Hotel</option>
                    <option>Boutique Retreat</option>
                    <option>Business Hotel</option>
                    <option>Resort / Villa</option>
                    <option>Restaurant / F&B</option>
                    <option>Residential / Clubhouse</option>
                    <option>Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs tracking-[0.2em] uppercase text-[#6b5847] mb-2">Tell us about the project</label>
                <textarea
                  name="message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Scope, key count, timelines, references — anything that helps us understand the requirement."
                  className="w-full px-0 py-3 bg-transparent border-b border-[#c9bfae] focus:border-[#1a1614] outline-none text-[#1a1614] transition-colors resize-none placeholder:text-[#a89a86]"
                />
              </div>

              <button
                type="submit"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#1a1614] text-[#faf6f0] hover:bg-[#a67c3d] transition-colors text-sm tracking-wide"
              >
                Send Enquiry <Send size={15} />
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Map — RCD Jaitpura location */}
      <section className="pb-24 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-6 gap-3">
            <div>
              <p className="text-[10px] md:text-xs tracking-[0.32em] uppercase text-[#a67c3d] mb-2">
                Find Us
              </p>
              <h2 className="font-serif text-3xl md:text-5xl text-[#1a1614] leading-tight">
                RCD Jaipur <span className="italic text-[#a67c3d]">&mdash; Jaitpura unit.</span>
              </h2>
            </div>
            <a
              href="https://www.google.com/maps/search/?api=1&query=Khasra+No.+1955-1965+Jaitpura+Jaipur+Rajasthan+303704"
              target="_blank"
              rel="noreferrer"
              data-testid="contact-open-in-maps"
              className="rcd-link text-sm text-[#1a1614] font-medium"
            >
              Open in Google Maps &rarr;
            </a>
          </div>

          <div
            className="relative w-full overflow-hidden bg-[#e8dfd1] shadow-[0_25px_60px_-25px_rgba(0,0,0,0.35)] border border-[#e0d6c5]"
            style={{ aspectRatio: '16 / 9' }}
            data-testid="contact-map-frame"
          >
            <iframe
              title="RCD Jaipur — Jaitpura unit location"
              src="https://maps.google.com/maps?q=Rajdhani+Crafts+and+Daughters+Pvt+Ltd,+Khasra+No+1955-1965,+Jaitpura,+Jaipur,+Rajasthan+303704&hl=en&ll=27.0065,75.7862&z=15&t=m&output=embed"
              width="100%"
              height="100%"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="absolute inset-0 border-0"
              allowFullScreen
            />

            {/* Visible balloon overlay — reinforces the GPS pin above the embedded map */}
            <a
              href="https://www.google.com/maps/dir/?api=1&destination=Rajdhani+Crafts+and+Daughters+Pvt+Ltd,+Jaitpura,+Jaipur,+Rajasthan+303704&destination_place_id="
              target="_blank"
              rel="noreferrer"
              aria-label="Get directions to RCD Jaipur"
              data-testid="map-balloon-pin"
              className="group absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-full flex flex-col items-center pointer-events-auto"
            >
              <div className="rcd-map-callout bg-[#1a1614] text-[#faf6f0] px-4 py-2 shadow-xl border border-[#a67c3d] whitespace-nowrap">
                <p className="text-[9px] tracking-[0.28em] uppercase text-[#d4b483] leading-none">RCD Jaipur</p>
                <p className="font-serif text-sm leading-tight mt-1">
                  27.0065&deg; N &middot; 75.7862&deg; E
                </p>
                <span className="rcd-map-callout-tail" aria-hidden="true" />
              </div>
              <span className="rcd-map-pin group-hover:scale-110 transition-transform" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="34" height="34" fill="#a67c3d" stroke="#1a1614" strokeWidth="1.5">
                  <path d="M12 2C7.58 2 4 5.58 4 10c0 6 8 12 8 12s8-6 8-12c0-4.42-3.58-8-8-8Zm0 11a3 3 0 1 1 0-6 3 3 0 0 1 0 6Z" />
                </svg>
              </span>
              <span className="rcd-map-pulse" aria-hidden="true" />
            </a>
          </div>

          <p className="mt-4 text-xs md:text-sm text-[#6b5847] leading-relaxed max-w-3xl">
            {CONTACT_INFO.address}
          </p>
        </div>
      </section>
    </div>
  );
};

export default Contact;
